import React, { useState } from 'react';
import { Globe, Database, Clock, Zap, CheckCircle, Play } from 'lucide-react';

const TRIGGER_TYPES = [
  { id: 'webhook', label: 'Webhook Listener', icon: Globe, color: 'text-purple-600', description: 'Trigger on incoming HTTP webhook' },
  { id: 'db_change', label: 'Database Change', icon: Database, color: 'text-teal-600', description: 'Trigger on Supabase table events' },
  { id: 'schedule', label: 'Scheduled Execution', icon: Clock, color: 'text-blue-600', description: 'Run on a time-based schedule' },
  { id: 'api_call', label: 'External API Call', icon: Zap, color: 'text-orange-600', description: 'Trigger from external API request' },
];

const TriggerConfigPanel = ({ onTestTrigger }) => {
  const [selectedType, setSelectedType] = useState('webhook');
  const [config, setConfig] = useState({ webhookPath: '/api/trigger', table: 'funnel_leads', event: 'INSERT', schedule: '0 9 * * 1-5', apiKey: '' });
  const [tested, setTested] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    await new Promise(r => setTimeout(r, 1500));
    setTesting(false);
    setTested(true);
    onTestTrigger?.({ type: selectedType, config });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Trigger Configuration</h3>
        <p className="text-xs text-gray-500 mt-0.5">Define what starts this workflow</p>
      </div>
      <div className="p-4 space-y-3">
        {TRIGGER_TYPES?.map(t => (
          <button
            key={t?.id}
            onClick={() => setSelectedType(t?.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors ${
              selectedType === t?.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <t.icon className={`w-4 h-4 flex-shrink-0 ${t?.color}`} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900">{t?.label}</p>
              <p className="text-xs text-gray-500">{t?.description}</p>
            </div>
            {selectedType === t?.id && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
          </button>
        ))}
      </div>
      <div className="px-4 pb-4 space-y-3">
        {selectedType === 'webhook' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Webhook Path</label>
            <input
              type="text"
              value={config?.webhookPath}
              onChange={e => setConfig(p => ({ ...p, webhookPath: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
        {selectedType === 'db_change' && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Table</label>
              <select
                value={config?.table}
                onChange={e => setConfig(p => ({ ...p, table: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="funnel_leads">funnel_leads</option>
                <option value="user_profiles">user_profiles</option>
                <option value="workflows">workflows</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Event</label>
              <select
                value={config?.event}
                onChange={e => setConfig(p => ({ ...p, event: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="INSERT">INSERT</option>
                <option value="UPDATE">UPDATE</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
          </div>
        )}
        {selectedType === 'schedule' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cron Expression</label>
            <input
              type="text"
              value={config?.schedule}
              onChange={e => setConfig(p => ({ ...p, schedule: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Weekdays at 9am</p>
          </div>
        )}
        {selectedType === 'api_call' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">API Key (optional)</label>
            <input
              type="text"
              value={config?.apiKey}
              onChange={e => setConfig(p => ({ ...p, apiKey: e?.target?.value }))}
              placeholder="Leave blank to auto-generate"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        <button
          onClick={handleTest}
          disabled={testing}
          className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors ${
            tested ? 'bg-green-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
          } disabled:opacity-60`}
        >
          {testing ? (
            <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Testing...</>
          ) : tested ? (
            <><CheckCircle className="w-3.5 h-3.5" />Trigger Validated!</>
          ) : (
            <><Play className="w-3.5 h-3.5" />Test Trigger</>
          )}
        </button>
      </div>
    </div>
  );
};

export default TriggerConfigPanel;
